import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Skeleton, Typography } from 'antd';
import cn from 'classnames';

import { Card } from '@/shared/ui/card';

import { parseNegativeNumber } from '../../lib/parse-negative-number';
import { useGetTradingPlatformSessionCurrency } from '../../models/selectors/get-trading-platform-session-currency';

import cls from './trading-platform-grouped-metrics-card.module.css';

interface MetricItem {
    hasColor?: boolean;
    hasPrefix?: boolean;
    preferredColor?: 'up' | 'down';
    titleKey: string;
    value: string;
}

interface TradingPlatformGroupedMetricsCardProps {
    isLoading: boolean;
    metrics: MetricItem[];
    title: string;
}

export const TradingPlatformGroupedMetricsCard = memo(
    (props: TradingPlatformGroupedMetricsCardProps) => {
        const { isLoading, metrics, title } = props;

        const { t } = useTranslation('trading-journal');

        const currency = useGetTradingPlatformSessionCurrency();

        const renderMetric = (metric: MetricItem) => {
            const { hasColor, hasPrefix, preferredColor, titleKey, value } =
                metric;

            const currentValue = !hasPrefix
                ? value
                : parseNegativeNumber(+value, currency);

            const colorize = !hasColor
                ? preferredColor
                : preferredColor || (+value >= 0 ? 'up' : 'down');

            return (
                <Flex
                    key={titleKey}
                    className={cls.metricItem}
                    gap={4}
                    vertical
                >
                    <Typography.Text className={cls.metricLabel}>
                        {t(titleKey)}
                    </Typography.Text>
                    {isLoading ? (
                        <Skeleton.Input
                            active
                            className={cls.metricValue}
                            size="small"
                        />
                    ) : (
                        <Typography.Text
                            className={cn(cls.metricValue, {
                                [cls.down]: colorize === 'down',
                                [cls.up]: colorize === 'up',
                            })}
                        >
                            {currentValue}
                        </Typography.Text>
                    )}
                </Flex>
            );
        };

        return (
            <Card
                isFullHeight
                title={title}
                wrapperClassName={cls.root}
            >
                <Flex
                    className={cls.metricsContainer}
                    gap={16}
                    vertical
                >
                    {metrics.map(renderMetric)}
                </Flex>
            </Card>
        );
    },
);

TradingPlatformGroupedMetricsCard.displayName =
    'TradingPlatformGroupedMetricsCard';
